import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Button,
  Popover,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Chip,
} from "@mui/material";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import { toast } from "react-toastify";
import Pagination from "../pagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import { styled, useTheme } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { asnService } from "../../utils/services/asnService";
import { useNavigate } from "react-router-dom";
import PODetailsModal from "../po-details-modal";
import OrderTrackingIcon from "../../globalStyles/icons/order-tracking-icon";
import {
  getFormattedDate,
  formatPoDate,
  trailEventDate,
  formatDateTime,
} from "../../utils/formatDate";
import { isPipe17Po, buildPipe17Timeline } from "../../utils/pipe17PoTimeline";
import EditOffOutlinedIcon from "@mui/icons-material/EditOffOutlined";
import TableHeader from "../table-header";
import FilterModal from "../filterModal";
import { Send } from "@mui/icons-material";

const DottedStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "green",
      borderTopStyle: "dotted",
      borderTopWidth: 3,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "green",
      borderTopStyle: "dotted",
      borderTopWidth: 3,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: "grey",
    borderTopStyle: "dotted",
    borderTopWidth: 3,
    borderRadius: 1,
    ...theme.applyStyles("dark", {
      borderColor: theme.palette.grey[800],
    }),
  },
}));

/**
 * The expanded-row timeline.
 *
 * Takes an already-resolved `[{ label, date, reached }]` — see `timelineSteps`
 * — so the same stepper renders BOTH lifecycles: the portal's ten stages
 * (driven by asn-trail rows) and Pipe17's six timeline rows (driven by fields
 * on the ASN itself). The two differ only in the array handed in.
 *
 * `activeStep` is derived from the steps, not from the ASN's status: Pipe17's
 * rows are not a linear lifecycle, so looking a status up by label there would
 * find nothing and grey the whole card.
 *
 * It is the length of the LEADING RUN of reached steps, not the last reached
 * index, because MUI colours connectors purely by index against activeStep.
 * Pipe17's reached flags are not monotonic — a PO can carry an Ordered date
 * and no Expected Ship — and taking the last reached index there ran a green
 * line straight through the grey gap, reading as though the missing dates were
 * merely unlabelled. For the portal's ten stages the two are identical, since
 * those flags only ever go false-after-true.
 *
 * A reached row with no date renders its marker green and its stamp blank.
 * Two of Pipe17's six rows genuinely have no timestamp on the wire, and an
 * invented one would read as fact — see utils/pipe17PoTimeline.js.
 */
const POTimeline = ({ steps }) => (
  <Stepper
    activeStep={
      steps.findIndex((s) => !s.reached) === -1
        ? steps.length - 1
        : steps.findIndex((s) => !s.reached) - 1
    }
    alternativeLabel
    connector={<DottedStepConnector />}
  >
    {steps.map((step, trackIndex) => {
      const stamp = step.date ? getFormattedDate(step.date) : null;
      return (
        <Step key={`${step.label} ${trackIndex}`}>
          <StepLabel
            StepIconComponent={() => (
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: "bold",
                  backgroundColor: step.reached ? "green" : "grey",
                  color: "white",
                }}
              >
                {trackIndex + 1}
              </div>
            )}
          >
            <div
              style={{ display: "flex", flexDirection: "column" }}
              // The ASN status this row stands for. Only the Pipe17 rows carry
              // one, and only there is the binding non-obvious — the portal's
              // ten stages ARE their statuses.
              title={
                step.status && step.status !== step.label
                  ? `Status: ${step.status}`
                  : undefined
              }
            >
              <div>{step.label}</div>
              <div>
                <span style={{ marginTop: 10 }}>
                  {stamp ? stamp.time : ""}
                  <br />
                  {stamp ? stamp.dateStr : ""}
                </span>
              </div>
            </div>
          </StepLabel>
        </Step>
      );
    })}
  </Stepper>
);

const POTableCell = ({
  asn,
  handleViewClick,
  handleEditClick,
  handleDeleteClick,
  handleTrackClick,
  handleUpdateStatusClick,
  handleReNotify,
  roles,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "view":
        handleViewClick(asn);
        break;
      case "edit":
        handleEditClick(asn);
        break;
      case "delete":
        handleDeleteClick(asn);
        break;
      case "track":
        handleTrackClick(asn);
        break;
      case "status":
        handleUpdateStatusClick(asn);
        break;
      case "re-notify":
        handleReNotify(asn);
        break;
      default:
        break;
    }
  };

  const canUpdate = (asn) => {
    if (["Stock-in Complete", "Cancelled"].includes(asn.status)) {
      return false;
    } else if (
      !asn.order_placed ||
      !asn.email_sent ||
      (!asn.additional_info?.driver_name &&
        !asn.additional_info?.vehicle_license &&
        roles.some((a) => a.name === "Admin"))
    ) {
      return true;
    }
  };

  return (
    <Box>
      <IconButton
        aria-label="more actions"
        onClick={handlePopoverOpen}
        sx={{
          color: open ? "primary.main" : "inherit",
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "10px",
          }}
        >
          <Button
            style={{
              color: "var(--text-tertiary)",
              justifyContent: "flex-start",
              textTransform: "none",
            }}
            aria-label="view"
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick("view");
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <ViewIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"var(--text-tertiary)"}
                fontWeight={500}
              >
                View
              </Typography>
            </Box>
          </Button>
          <Button
            style={{
              justifyContent: "flex-start",
              textTransform: "none",
            }}
            aria-label="track"
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick("track");
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <OrderTrackingIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                fontWeight={500}
                color={"var(--text-tertiary)"}
              >
                Track
              </Typography>
            </Box>
          </Button>

          <Button
            style={{
              color: "var(--text-tertiary)",
              justifyContent: "flex-start",
              textTransform: "none",
              cursor: canUpdate(asn) ? "pointer" : "not-allowed",
            }}
            aria-label="edit"
            onClick={(e) => {
              e.stopPropagation();
              if (canUpdate(asn)) {
                handleActionClick("edit");
              }
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              {canUpdate(asn) ? (
                <EditIcon fill="#FEC50F" />
              ) : (
                <EditOffOutlinedIcon fill="gray" />
              )}

              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                fontWeight={500}
                color={canUpdate(asn) ? "var(--text-tertiary)" : "gray"}
              >
                Update
              </Typography>
            </Box>
          </Button>
          {[
            // "Created",
            // "Reached Warehouse",
            // "Supplier Confirmed",
            // "Inventory Ready",
          ].includes(asn?.status) ? (
            <Tooltip
              title={
                !asn?.supplier
                  ? "No supplier assigned"
                  : asn.status === "Created"
                    ? "PO pending supplier confirmation — follow up required"
                    : asn.status === "Reached Warehouse"
                      ? "Send a reminder email to the warehouse about this PO pending processing."
                      : asn.status === "Inventory Ready"
                        ? "Notify operations team to coordinate pickup with delivery partner."
                        : "Dispatch date passed — follow up with supplier for processing."
              }
              placement="bottom"
            >
              <Button
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
                disabled={!asn?.supplier}
                onClick={() => handleActionClick("re-notify")}
                sx={{ textTransform: "none", justifyContent: "flex-start" }}
              >
                <Box display={"flex"} gap={1} alignItems={"center"}>
                  <Send />

                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={"var(--text-tertiary)"}
                    fontWeight={500}
                  >
                    {asn.status === "Created"
                      ? "Re-Notify"
                      : asn.status === "Reached Warehouse"
                        ? "Alert Warehouse"
                        : asn.status === "Inventory Ready"
                          ? "Notify Ops"
                          : asn.status === "Supplier Confirmed" &&
                              new Date(
                                asn?.confirmed_dispatch_date ||
                                  asn?.expected_dispatch_date,
                              ).getTime() < new Date().getTime()
                            ? "Follow-up"
                            : "Notify"}
                  </Typography>
                </Box>
                <Typography
                  fontFamily={"poppins"}
                  fontSize={"10px"}
                  color={"#33333380"}
                  fontWeight={500}
                >
                  {["Created", "Supplier Confirmed"].includes(asn.status) &&
                  asn?.email_sent_date
                    ? `Last sent @ ${formatDateTime(asn.email_sent_date)}`
                    : asn.status === "Inventory Ready" &&
                        asn?.inventory_ready_notify_date
                      ? `Last sent @ ${formatDateTime(asn.inventory_ready_notify_date)}`
                      : asn.status === "Reached Warehouse" &&
                          asn?.inbound_delay_notify_date
                        ? `Last sent @ ${formatDateTime(asn.inbound_delay_notify_date)}`
                        : ""}
                </Typography>
              </Button>
            </Tooltip>
          ) : (
            <></>
          )}
          <Button
            style={{
              justifyContent: "flex-start",
              textTransform: "none",
            }}
            aria-label="delete"
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick("delete");
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <DeleteIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                fontWeight={500}
                color={"var(--text-tertiary)"}
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    // border: "1px solid #EAECF0",
    // color: theme.palette.common.black,
    padding: "6px",
    textAlign: "start",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "4px",
    textAlign: "start",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    // border: "1px solid #EAECF0",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    // border: "1px solid #EAECF0",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));
const PurchaseOrderList = ({
  viewMode,
  data,
  onEditData,
  onDeleteData,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  selectedAsn,
  setSelectedAsn,
  orderType,
  filterData,
  onChangeFilter,
  roles,
}) => {
  const [asnData, setAsnData] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const startIndex = rowsPerPage * page;
  const navigate = useNavigate();
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const [expandedRow, setExpandedRow] = useState({});
  const [asnTrail, setAsnTrail] = useState([]);
  const clientId = localStorage.getItem("client_id");
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [selectedFilterKey, setSelectedFilterKey] = useState("");
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    name: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    options: [],
    loadMoreButton: false,
    searchKey: "",
  });

  const handleOpenFilter = (event, filterKey) => {
    console.log(event, filterKey);

    setAnchorElFilter(event.currentTarget);
    setSelectedFilterKey(filterKey);
    setOpenFilterModal(true);
    setSelectedFilterData(
      filterData?.columns.find((x) => x?.key === filterKey),
    );
  };
  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const deleteData = async () => {
    try {
      await asnService.delete(selectedAsn._id);
      setOpenConfirmation(false);
      onDeleteData && onDeleteData();
      toast.success("ASN deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete");
      console.error("Delete failed:", error);
    }
  };
  const updateFilterData = () => {
    localStorage.setItem(
      "po-filter-data",
      JSON.stringify({
        populate: filterData.populate,
        filters: filterData.columns.map((x) => ({
          key: x.key,
          values: x.filter,
        })),
        limit: filterData.limit,
      }),
    );
  };

  const handleEditClick = (asn) => {
    updateFilterData();
    localStorage.setItem("path", `/add-asn/${asn._id}`);
    navigate(`/update-asn/${asn._id}`);
  };
  const handleViewClick = (shipping) => {
    setSelectedAsn(shipping);
    setOpenViewModal(true);
  };
  const handleDeleteClick = (item) => {
    setSelectedAsn(item);
    setOpenConfirmation(true);
  };
  const handleTrackClick = (asn) => {
    setSelectedAsn(asn);
    updateFilterData();
    navigate(`/asn-tracking/${asn._id}`);
  };

  const handleReNotify = async (x) => {
    try {
      if (x.status === "Created") {
        await asnService.reNotifySupplier(x._id);
        toast.success("Supplier has been notified successfully");
      } else if (x.status === "Reached Warehouse") {
        await asnService.reNotifyWarehouse(x._id);
        toast.success("Warehouse has been notified successfully");
      } else if (x.status === "Inventory Ready") {
        await asnService.notifyInventoryReady(x._id);
        toast.success(
          "Operations team has been notified for pickup coordination",
        );
      } else if (x.status === "Supplier Confirmed") {
        await asnService.reNotifySupplierDispatch(x._id);
        toast.success("Supplier has been notified successfully");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateStatus = async (asn) => {
    try {
      let response = await asnService.edit(asn._id, {
        status:
          dummyTrackingDetails[
            dummyTrackingDetails.find((x) => x.label === asn.status)?.sl
          ].label,
      });
      await onDeleteData();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setAsnData(
      data?.map((asn, index) => ({
        ...asn,
        action: (
          <POTableCell
            key={`${asn._id} ${index}`}
            asn={asn}
            handleViewClick={handleViewClick}
            handleEditClick={handleEditClick}
            handleDeleteClick={handleDeleteClick}
            handleTrackClick={handleTrackClick}
            handleUpdateStatusClick={handleUpdateStatus}
            handleReNotify={handleReNotify}
            roles={roles}
          />
        ),
      })),
    );
  }, [data]);

  // Mirrors the LINEAR inbound path of control-tower's ASN_STATUSES
  // (utils/constants.js) and must keep the same `sl` ranks — the trail is
  // built by comparing them, so a drift here silently truncates the stepper.
  // Off-path states (Pending, On Hold, Returned to Supplier, Cancelled) are
  // deliberately absent: they are not steps, and fetchAsnTrail falls back for
  // them rather than rendering them as stages.
  let dummyTrackingDetails = [
    { label: "Created", sl: 10 },
    { label: "Supplier Notified", sl: 20 },
    { label: "Supplier Confirmed", sl: 30 },
    { label: "Partially Fulfilled", sl: 35 },
    { label: "Inventory Ready", sl: 40 },
    { label: "Pickup Scheduled", sl: 50 },
    { label: "Picked Up", sl: 60 },
    { label: "Reached Warehouse", sl: 70 },
    { label: "Partially Received", sl: 75 },
    { label: "Stock-in Complete", sl: 80 },
  ];

  const fetchAsnTrail = async (id) => {
    let asnList = await asnService.fetchAsnTrail(id);
    // Rank the stages the ASN actually reached and take the furthest — never
    // read the furthest stage off the END OF THE ARRAY. /fetch-asn-trail sorts
    // on `parent.createdAt`, which is when WE wrote the row, so a backfilled
    // or caught-up trail carries the SAME createdAt on every row; the sort is
    // then a full tie, Mongo returns the group in arbitrary order, and the
    // `last_sl?.sl >= x.sl` filter below truncates the trail at whichever
    // stage happened to land last (seen live: a 7-stage PO rendering 5).
    //
    // Ranking also handles the case this fallback was originally written for:
    // an entry sitting OFF the linear path (On Hold, Cancelled, Returned to
    // Supplier) ranks as undefined and drops out, instead of matching nothing
    // and blanking the whole stepper.
    const last_sl = asnList
      .map((entry) =>
        dummyTrackingDetails.find((x) => x.label === entry.status),
      )
      .filter(Boolean)
      .reduce((a, b) => (b.sl > a.sl ? b : a), { sl: -1 });
    let updated_trail = dummyTrackingDetails
      .filter((x) => last_sl?.sl >= x.sl)
      .map((x) => {
        let asn_trail = asnList.find((xx) => xx.status == x.label);
        if (asn_trail) {
          return asn_trail;
        } else {
          return {
            status: x.label,
            parent: getPreviousParentObject(asnList, x),
          };
        }
      });
    setAsnTrail(updated_trail);
  };

  const getPreviousParentObject = (asnList, track) => {
    let remain_track = dummyTrackingDetails
      .filter((x) => x.sl > track.sl)
      ?.map((x) => x.label)
      ?.toReversed();

    return {
      ...asnList.find((x) => remain_track.includes(x.status))?.parent,
      status: track.label,
    };
  };

  /**
   * Which timeline an expanded PO row shows.
   *
   * Pipe17-imported POs get Pipe17's own six rows; everything else keeps the
   * portal's ten-stage lifecycle. The split is not cosmetic — the ten stages
   * are driven by asn-trail rows, and a provider PO has none of them
   * (control-tower's oms-sync upsert uses `_patch`, which bypasses the asn
   * hooks that publish the trail), so rendering them for a Pipe17 PO shows an
   * entirely grey stepper no matter how far upstream the PO has actually got.
   */
  const timelineSteps = (asn) => {
    if (isPipe17Po(asn)) return buildPipe17Timeline(asn);
    const currentSl = dummyTrackingDetails.find(
      (s) => s.label === asn.status,
    )?.sl;
    return dummyTrackingDetails.map((step) => {
      // Prefer the real event time over the row's write time — see
      // trailEventDate.
      const reachedRow = asnTrail.find((t) => t.status === step.label);
      return {
        label: step.label,
        date: reachedRow ? trailEventDate(reachedRow.parent) : null,
        reached: currentSl !== undefined && step.sl <= currentSl,
      };
    });
  };

  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {asnData?.map((x, index) => (
        <Grid
          sx={{ paddingBottom: 1 }}
          item
          xs={12}
          sm={6}
          md={4}
          key={`${x._id} ${index}`}
        >
          <Card
            sx={{
              boxShadow: 3,
              cursor: "pointer",
              width: "100%",
              height: "100%",
              position: "relative",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
            }}
          >
            <CardContent>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "start" }}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  {startIndex + index + 1}
                </Typography>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  {x?.action}
                </div>
              </div>
              <Grid container spacing={1} mt={1}>
                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    P.O. Number
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    : {x?.asn_no}
                  </Typography>
                </Grid>
                {orderType === "M2O" && (
                  <Grid item xs={5} display="flex" alignItems="center">
                    <Typography
                      fontWeight="500"
                      sx={{ textAlign: "start" }}
                      fontFamily="Poppins"
                      color="var(--text-tertiary)"
                    >
                      Order ID
                    </Typography>
                  </Grid>
                )}
                {orderType === "M2O" && (
                  <Grid item xs={5} display="flex" alignItems="center">
                    <Typography
                      fontWeight="500"
                      sx={{ textAlign: "start" }}
                      fontFamily="Poppins"
                      color="var(--text-tertiary)"
                    >
                      : {x?.order_id}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    SKU
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    : {x?.sku_list?.map((x) => x.sku).join(", ")}
                  </Typography>
                </Grid>
                {!clientId ? (
                  <>
                    <Grid item xs={5} display={"flex"} alignItems={"center"}>
                      <Typography
                        fontWeight="500"
                        sx={{ textAlign: "start" }}
                        fontFamily={"poppins"}
                        color={"var(--text-tertiary)"}
                      >
                        Client
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography
                        noWrap
                        sx={{ textAlign: "start" }}
                        fontWeight="500"
                        fontFamily={"poppins"}
                        color={"var(--text-tertiary)"}
                      >
                        : {x?.client?.business_name}
                      </Typography>
                    </Grid>
                  </>
                ) : (
                  <></>
                )}

                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    Supplier
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    : {x?.supplier?.name}
                  </Typography>
                </Grid>

                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    PO Date
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    : {formatPoDate(x)}
                  </Typography>
                </Grid>

                <Grid item xs={5} display={"flex"} alignItems={"center"}>
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    Status
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    : {x?.status}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", height: "300px" }}
      />
    </div>
  );

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    ...(orderType === "M2O"
      ? [
          {
            key: "asn_no",
            title: "P.O. NO",
            handleOpenFilter: handleOpenFilter,
            displayKey: "asn_no",
          },
        ]
      : [
          {
            key: "asn_no",
            title: "P.O. NO",
            handleOpenFilter: handleOpenFilter,
            render: (item, index) => (
              <Box>
                <Typography
                  fontSize="12px"
                  color="var(--text-secondary)"
                  fontWeight={400}
                  fontFamily="Poppins"
                  sx={{
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                  }}
                >
                  {item.asn_no}
                </Typography>
                {item?.order_id ? (
                  <Chip
                    label={item?.order_id}
                    size="small"
                    sx={{
                      fontFamily: "poppins",
                      fontWeight: 500,
                      color: "var(--text-tertiary)",
                    }}
                  />
                ) : null}
              </Box>
            ),
          },
        ]),
    ...(orderType === "M2O"
      ? [
          {
            key: "order_id",
            title: "ORDER",
            displayKey: "order_id",
            handleOpenFilter: handleOpenFilter,
          },
        ]
      : []),
    {
      key: "sku_list.sku",
      title: "SKU",
      handleOpenFilter: handleOpenFilter,
      render: (x) => (
        <Tooltip title={x?.sku_list?.map((sku) => sku.sku).join(", ")} arrow>
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontWeight={400}
            fontFamily="Poppins"
            sx={{
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "150px",
            }}
          >
            {x?.sku_list?.length > 2
              ? `${x.sku_list
                  .slice(0, 2)
                  .map((sku) => sku.sku)
                  .join(", ")}...`
              : x?.sku_list?.map((sku) => sku.sku).join(", ")}
          </Typography>
        </Tooltip>
      ),
    },

    ...(!clientId
      ? [
          {
            key: "client.business_name",
            title: "CLIENT",
            displayKey: "client.business_name",
            handleOpenFilter: handleOpenFilter,
            key: "client",
          },
        ]
      : []),

    {
      key: "supplier",
      title: "SUPPLIER",
      handleOpenFilter: handleOpenFilter,
      displayKey: "supplier.name",
    },
    {
      key: "po_date",
      title: "PO DATE",
      handleOpenFilter: handleOpenFilter,
      render: (x) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {formatPoDate(x)}
        </Typography>
      ),
    },
    {
      key: "status",
      title: "STATUS",
      handleOpenFilter: handleOpenFilter,
      displayKey: "status",
    },

    { key: "action", title: "", displayKey: "action", width: "3%" },
  ];

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        // paddingBottom: 10,
      }}
    >
      <TableContainer
        sx={{
          overflowY: "auto",
          // paddingInline: 0.4,
          maxHeight: "100%",
        }}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            border: "1px solid var(--border-color)",
          }}
        >
          <TableHead
            sx={{
              backgroundColor: "var(--table-header-bg) !important",
              "& th": { backgroundColor: "var(--table-header-bg)" },
            }}
          >
            <StyledTableRow>
              {TableSchema.map((column) => (
                <TableHeader
                  filterData={filterData}
                  primary_color={primary_color}
                  handleOpenFilter={column.handleOpenFilter}
                  key={column.key}
                  filterKey={column.key}
                  title={column.title}
                />
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {asnData?.length === 0 ? (
              <StyledTableRow>
                <TableCell colSpan={8} align="center">
                  <NoDataImage />
                </TableCell>
              </StyledTableRow>
            ) : (
              asnData?.map((x, rowIndex) => (
                <>
                  <StyledTableRow
                    key={`${x._id} ${rowIndex}`}
                    style={{
                      cursor: x.status === "Pending" ? "inherit" : "pointer",
                    }}
                    onClick={() => {
                      if (x.status === "Pending") return;
                      if (expandedRow._id === x._id) {
                        setExpandedRow({});
                        setAsnTrail([]);
                      } else {
                        setExpandedRow(x);
                        fetchAsnTrail(x._id);
                      }
                    }}
                  >
                    {TableSchema.map((column, colIndex) => (
                      <StyledTableCell
                        key={column.key || colIndex}
                        sx={{ width: column.width }}
                      >
                        {column.displayKey ? (
                          <Typography
                            fontSize="12px"
                            color="var(--text-secondary)"
                            fontWeight={400}
                            fontFamily={"Poppins"}
                          >
                            {getNestedValue(x, column.displayKey) || "--"}
                          </Typography>
                        ) : column.render ? (
                          column.render(x, rowIndex)
                        ) : (
                          "--"
                        )}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                  {x._id === expandedRow._id ? (
                    <TableRow key={x.id}>
                      <TableCell
                        style={{ width: "100%" }}
                        colSpan={8}
                        //style={{ padding: 0, borderBottom: "none" }}
                      >
                        <Collapse
                          in={expandedRow._id === x._id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            marginBottom={2}
                            marginTop={1}
                            sx={{
                              width: "100%",
                              justifyContent: "center",
                              alignItems: "center",
                              display: "flex",
                              minHeight: 70,
                            }}
                          >
                            <Box
                              marginBottom={2}
                              marginTop={1}
                              sx={{
                                width: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                                display: "flex",
                                minHeight: 70,
                              }}
                            >
                              <Box sx={{ width: "100%" }}>
                                <POTimeline steps={timelineSteps(x)} />
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <></>
                  )}
                </>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  return (
    <div sx={{ minWidth: "100%" }}>
      <div
        style={{
          overflowY: "scroll",
          display: "flex",
          maxHeight: isMobile() ? "calc(100vh - 250px)" : `calc(100vh - 250px)`,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::WebkitScrollbar": { width: 0 },
        }}
      >
        {viewMode === "grid" || isMobile()
          ? renderGridView()
          : renderListView()}
      </div>
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
          type={selectedfilterData?.type}
        />
      </Popover>
      <Pagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= rowsPerPage * (page + 1)}
      />
      <DeleteConfirmationModal
        DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedAsn?.asn_no} ?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={() => {
          deleteData();
        }}
      />
      <PODetailsModal
        asn={{
          ...selectedAsn,
          sku_list: selectedAsn?.sku_list.map((x) => ({
            ...x,
            variant: x?.product?.variants?.find((v) => v.sku === x.sku),
          })),
        }}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        isAdmin={roles.some((x) => x.name === "Admin")}
      />
    </div>
  );
};

export default PurchaseOrderList;
